import React, { useState, useEffect } from 'react';

/**
 * A reusable form component for entity management
 * 
 * @param {Object} props
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onSubmit - Function to call on form submission
 * @param {boolean} props.isLoading - Whether the form is in loading state
 * @param {Array} props.fields - Array of field definitions
 * @param {string} props.submitButtonText - Text for the submit button
 * @param {string} props.title - Form title
 */
const EntityForm = ({ 
  initialValues = {}, 
  onSubmit, 
  isLoading = false, 
  fields = [], 
  submitButtonText = 'Save',
  title = 'Entity Form'
}) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  // Update form values when initialValues changes
  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for the field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    
    if (value === '' || value === '-') {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setFormValues(prev => ({
        ...prev,
        [name]: numValue
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formValues[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Submit form
    onSubmit(formValues);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      
      <form onSubmit={handleSubmit}>
        {fields.map(field => (
          <div key={field.name} className="mb-4">
            <label 
              htmlFor={field.name} 
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            
            {field.type === 'textarea' ? (
              <textarea
                id={field.name}
                name={field.name}
                value={formValues[field.name] || ''}
                onChange={handleChange}
                disabled={isLoading}
                rows={field.rows || 3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={field.placeholder || ''}
              />
            ) : field.type === 'select' ? (
              <select
                id={field.name}
                name={field.name}
                value={formValues[field.name] || ''}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select {field.label}</option>
                {field.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'number' ? (
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formValues[field.name] === undefined ? '' : formValues[field.name]}
                onChange={handleNumberChange}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={field.placeholder || ''}
                min={field.min}
                max={field.max}
                step={field.step || 1}
              />
            ) : field.type === 'date' ? (
              <input
                type="date"
                id={field.name}
                name={field.name}
                value={formValues[field.name] || ''}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            ) : field.type === 'checkbox' ? (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={field.name}
                  name={field.name}
                  checked={formValues[field.name] || false}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor={field.name} className="ml-2 text-sm text-gray-700">
                  {field.checkboxLabel || field.label}
                </label>
              </div>
            ) : (
              <input
                type={field.type || 'text'}
                id={field.name}
                name={field.name}
                value={formValues[field.name] || ''}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={field.placeholder || ''}
              />
            )}
            
            {errors[field.name] && (
              <p className="mt-1 text-sm text-red-600">{errors[field.name]}</p>
            )}
          </div>
        ))}
        
        <div className="flex justify-end mt-6">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EntityForm; 